import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card, LoadingState, Pill } from "@/components/common/UI";
import { fmtIN, fmtINR, fmtRelative } from "@/lib/format";
import {
    FileText, Hammer, Wallet, Calculator, AlertTriangle, Clock,
    CheckCircle, TrendingUp, TrendingDown, Factory, AlertCircle, ArrowUpRight,
    Plus, ArrowRight, Package, Layers, Users, Bell,
} from "lucide-react";

const todayDDMMYYYY = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

function Tile({ label, value, sub, accent, icon: Icon, onClick, testId, tone = "slate", footerLink, footerText }) {
    const toneCls = {
        slate: "text-slate-900",
        copper: "text-[hsl(var(--copper))]",
        emerald: "text-emerald-600",
        amber: "text-amber-600",
        red: "text-red-600",
    }[tone];
    return (
        <Card hover className="p-5 cursor-pointer transition-shadow" onClick={onClick} testId={testId}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="eyebrow text-slate-500">{label}</div>
                    <div className={`font-display text-[32px] font-semibold tabular-nums mt-1 ${toneCls} truncate`}>
                        {value ?? "—"}{accent && <span className="text-[12px] text-slate-500 font-mono ml-1">{accent}</span>}
                    </div>
                    {sub && <div className="text-[12px] text-slate-500 mt-1.5 leading-snug">{sub}</div>}
                </div>
                {Icon && <Icon className="h-5 w-5 text-slate-300 shrink-0" strokeWidth={1.8} />}
            </div>
            {footerLink && (
                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[11px] text-[hsl(var(--copper))] font-medium">{footerText || "View"}</span>
                    <ArrowRight className="h-3 w-3 text-[hsl(var(--copper))]" />
                </div>
            )}
        </Card>
    );
}

function ActionCard({ icon: Icon, label, description, onClick, testId }) {
    return (
        <Card hover className="p-4 cursor-pointer group" onClick={onClick} testId={testId}>
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-[hsl(var(--copper))]/10 text-[hsl(var(--copper))] flex items-center justify-center group-hover:bg-[hsl(var(--copper))] group-hover:text-white transition-colors">
                    <Icon className="h-4 w-4" />
                </div>
                <div>
                    <div className="font-display text-[14px] font-semibold text-slate-900">{label}</div>
                    <div className="text-[11px] text-slate-500">{description}</div>
                </div>
            </div>
        </Card>
    );
}

function statusPill(s) {
    const map = {
        DRAFT: { tone: "slate", label: "Draft" },
        PENDING_DH: { tone: "amber", label: "DH Review" },
        PENDING_DIRECTOR: { tone: "amber", label: "Director" },
        APPROVED: { tone: "emerald", label: "Approved" },
        DIRECTOR_APPROVED: { tone: "emerald", label: "Locked" },
        HELD: { tone: "red", label: "Held" },
        DIRECTOR_REVIEW: { tone: "amber", label: "Director Review" },
        HELD_BY_DIRECTOR: { tone: "red", label: "Held" },
    };
    const e = map[s] || { tone: "slate", label: s };
    return <Pill tone={e.tone}>{e.label}</Pill>;
}

function marginPill(m) {
    if (m == null) return null;
    if (m >= 12) return <Pill tone="emerald">{m.toFixed(1)}%</Pill>;
    if (m >= 8) return <Pill tone="amber">{m.toFixed(1)}%</Pill>;
    return <Pill tone="red">{m.toFixed(1)}%</Pill>;
}

// ============================================================
// ANOMALY DETECTOR — statistical outliers over VERIFIED ZBCs.
// Self-fetching so it can drop into any role's dashboard without
// threading its data through the shared /dashboard/kpis payload.
// Renders nothing on error/empty — a quiet widget, not a blocking one.
// ============================================================
const ANOMALY_TYPE_META = {
    COST_OUTLIER_HIGH: { tone: "red", label: "Cost spike" },
    COST_OUTLIER_LOW: { tone: "amber", label: "Unusually low" },
    SUSPICIOUS_QUOTE: { tone: "red", label: "Below should-cost" },
};

function anomalyReason(a) {
    if (a.anomaly_type === "SUSPICIOUS_QUOTE") {
        return `Supplier quoted ${fmtINR(a.supplier_quote || 0)} vs should-cost ${fmtINR(a.should_cost || 0)} (${a.variance_pct != null ? `${a.variance_pct.toFixed(1)}%` : "—"} variance)`;
    }
    return `${fmtINR(a.cost_per_kg || 0)}/kg vs ${fmtINR(a.category_median_cost_per_kg || 0)}/kg median for ${a.rm_category} (${a.deviation_pct >= 0 ? "+" : ""}${a.deviation_pct}%)`;
}

function AnomalyDetectorCard({ navigate }) {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        api.get("/dashboard/anomalies").then((r) => setData(r.data)).catch(() => setFailed(true));
    }, []);

    if (failed || !data || !data.anomalies || data.anomalies.length === 0) return null;
    const items = data.anomalies.slice(0, 5);

    return (
        <Section eyebrow="ANOMALY DETECTOR">
            <Card className="p-5" testId="kpi-anomaly-detector">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="font-display text-[15px] font-semibold text-slate-900">Should-cost anomalies</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            {data.anomaly_count} flagged across {data.total_verified_zbcs} verified ZBCs &mdash; cost-per-kg outliers within category, plus below-should-cost supplier quotes.
                        </div>
                    </div>
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                </div>
                <div className="divide-y divide-slate-100">
                    {items.map((a) => {
                        const meta = ANOMALY_TYPE_META[a.anomaly_type] || { tone: "slate", label: a.anomaly_type };
                        return (
                            <button
                                key={`${a.zbc_id}-${a.anomaly_type}`}
                                onClick={() => navigate(`/zbc/${a.zbc_id}`)}
                                className="w-full flex items-center justify-between py-2.5 gap-3 text-left hover:bg-slate-50 -mx-1 px-1 rounded-md"
                                data-testid={`anomaly-row-${a.zbc_id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono font-semibold text-[13px] text-slate-900">{a.part_number || a.zbc_id}</span>
                                        <Pill tone={meta.tone}>{meta.label}</Pill>
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                                        {a.customer_name || "—"} · {anomalyReason(a)}
                                    </div>
                                    {a.ai_note && <div className="text-[11px] text-[hsl(var(--copper))] mt-0.5 italic">{a.ai_note}</div>}
                                </div>
                                <ArrowRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                            </button>
                        );
                    })}
                </div>
            </Card>
        </Section>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hour] = useState(new Date().getHours());

    useEffect(() => {
        api.get("/dashboard/kpis").then((r) => setKpis(r.data)).finally(() => setLoading(false));
    }, []);

    if (!user) return null;
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    const firstName = user.name?.split(" ")[0] || "there";

    return (
        <div data-testid="dashboard-page" className="space-y-7">
            {/* Greeting */}
            <div>
                <div className="eyebrow text-slate-500 mb-1.5">CO STIQ · {todayDDMMYYYY()}</div>
                <h1 className="font-display text-3xl lg:text-[36px] font-semibold text-slate-900 leading-tight">
                    {greeting}, <span className="text-[hsl(var(--copper))]">{firstName}</span> <span className="text-slate-300">👋</span>
                </h1>
                <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">
                    Here&rsquo;s where ABC COMPANY&rsquo;s cost intelligence stands right now &mdash; across plants, customers, and the open pricing pipeline.
                </p>
            </div>

            {loading || !kpis ? (
                <LoadingState label="Loading dashboard" />
            ) : (
                <>
                    {user.role === "CORPORATE_ADMIN" && <AdminDashboard kpis={kpis} navigate={navigate} />}
                    {user.role === "FINANCE_PRICING" && <FinanceDashboard kpis={kpis} navigate={navigate} />}
                    {user.role === "DEPT_HEAD" && <DeptHeadDashboard kpis={kpis} navigate={navigate} />}
                    {user.role === "DIRECTOR" && <DirectorDashboard kpis={kpis} navigate={navigate} />}
                    {user.role === "PLANT_MANAGER" && <PlantDashboard kpis={kpis} navigate={navigate} />}
                    {user.role === "PURCHASE_MANAGER" && <PurchaseDashboard kpis={kpis} navigate={navigate} />}
                </>
            )}
        </div>
    );
}

// ============================================================
// ADMIN DASHBOARD — sees all
// ============================================================
function AdminDashboard({ kpis, navigate }) {
    return (
        <>
            {/* Row 1: Activity */}
            <Section eyebrow="ACTIVITY OVERVIEW">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Tile
                        label="ZBCs THIS MONTH" icon={Calculator}
                        value={fmtIN(kpis.zbcs_this_month, { decimals: 0 })} accent="created"
                        sub={`${kpis.zbcs_verified_this_month || 0} verified`}
                        onClick={() => navigate("/zbc")}
                        footerLink footerText="View ZBC list" testId="kpi-admin-zbcs"
                    />
                    <Tile
                        label="RFQS IN PROGRESS" icon={FileText}
                        value={fmtIN(kpis.open_rfqs, { decimals: 0 })}
                        sub={<>{kpis.rfqs_pending_approval || 0} pending approval · {kpis.rfqs_overdue > 0 ? <span className="text-red-600 font-medium">{kpis.rfqs_overdue} overdue ⚠</span> : "0 overdue"}</>}
                        tone={kpis.rfqs_overdue > 0 ? "amber" : "slate"}
                        onClick={() => navigate("/rfq")} footerLink footerText="View RFQ list" testId="kpi-admin-rfqs"
                    />
                    <Tile
                        label="SETTLEMENTS THIS QUARTER" icon={Wallet}
                        value={fmtIN(kpis.settlements_this_quarter || 0, { decimals: 0 })}
                        sub={<>{kpis.settlements_director_review || 0} Director sign-off · <span className="text-emerald-600">{kpis.settlements_locked || 0} locked ✓</span></>}
                        onClick={() => navigate("/settlement")} footerLink footerText="View settlements" testId="kpi-admin-settlements"
                    />
                </div>
            </Section>

            {/* Row 2: Alerts */}
            <Section eyebrow="ALERTS & FLAGS">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Tile
                        label="VARIANCE FLAGS" icon={AlertTriangle}
                        tone={kpis.variance_red > 0 ? "red" : "slate"}
                        value={fmtIN(kpis.variance_red || 0, { decimals: 0 })} accent="RED"
                        sub={`${kpis.variance_flag || 0} FLAG badges (supplier price too low to be credible)`}
                        onClick={() => navigate("/zbc/variance")} footerLink footerText="Variance report" testId="kpi-admin-variance"
                    />
                    <Tile
                        label="STALE SUPPLIER QUOTES" icon={Clock}
                        tone={kpis.stale_quotes_count > 0 ? "amber" : "slate"}
                        value={fmtIN(kpis.stale_quotes_count || 0, { decimals: 0 })} accent="> 180 days"
                        sub="Refresh before next ZBC update cycle"
                        onClick={() => navigate("/supplier-quotes")} footerLink footerText="Supplier quotes" testId="kpi-admin-stale"
                    />
                    <Tile
                        label="FOUNDATION GAPS" icon={Layers}
                        tone={(kpis.machines_missing_rate || 0) + (kpis.plants_inactive || 0) > 0 ? "amber" : "slate"}
                        value={fmtIN(kpis.machines_missing_rate || 0, { decimals: 0 })} accent="machines"
                        sub={`missing approved rate · ${kpis.plants_inactive || 0} plant${kpis.plants_inactive === 1 ? "" : "s"} INACTIVE`}
                        onClick={() => navigate("/machines")} footerLink footerText="Foundation setup" testId="kpi-admin-gaps"
                    />
                </div>
            </Section>

            {/* Row 3: Steel rate monitor */}
            {kpis.steel_rate_monitor && kpis.steel_rate_monitor.length > 0 && (
                <Section eyebrow="STEEL RATE MONITOR">
                    <Card className="p-5" testId="kpi-admin-steel-rate-monitor">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <div className="font-display text-[15px] font-semibold text-slate-900">Live grade tracker</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">Click a row to see history &amp; affected ZBCs.</div>
                            </div>
                            <button onClick={() => navigate("/steel-grades")} className="text-[11px] font-medium text-[hsl(var(--copper))] hover:underline">View all grades →</button>
                        </div>
                        <div className="space-y-2">
                            {kpis.steel_rate_monitor.map((g) => {
                                const delta = g.delta;
                                const trend = delta == null ? null : delta > 0.001 ? "up" : delta < -0.001 ? "down" : "flat";
                                return (
                                    <div key={g.grade_id} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-100"
                                         onClick={() => navigate("/steel-grades")}>
                                        <div className="flex-1">
                                            <div className="font-mono font-semibold text-[13px] text-slate-900">{g.code}</div>
                                            <div className="text-[11px] text-slate-500">{g.name}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono tabular-nums text-[15px] font-semibold text-slate-900">{fmtINR(g.rate_per_kg || 0)}/kg</div>
                                            <div className="text-[11px] flex items-center justify-end gap-1 mt-0.5">
                                                {trend === "up" && <><TrendingUp className="h-3 w-3 text-red-600" /><span className="text-red-600 font-mono">+{delta.toFixed(2)} vs last</span></>}
                                                {trend === "down" && <><TrendingDown className="h-3 w-3 text-emerald-600" /><span className="text-emerald-600 font-mono">{delta.toFixed(2)} vs last</span></>}
                                                {trend === "flat" && <span className="text-slate-400">→ No change</span>}
                                                {trend == null && <span className="text-slate-400">First rate</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {kpis.last_rate_change && (
                            <div className="text-[11px] text-slate-400 mt-3 pt-3 border-t border-slate-100">
                                Last updated by <span className="text-slate-600 font-medium">{kpis.last_rate_change.changed_by_name}</span> · {fmtRelative(kpis.last_rate_change.changed_at)}
                            </div>
                        )}
                    </Card>
                </Section>
            )}

            <AnomalyDetectorCard navigate={navigate} />

            {/* Row 4: Quick actions */}
            <Section eyebrow="QUICK ACTIONS">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ActionCard icon={Plus} label="New ZBC" description="Start a should-cost analysis" onClick={() => navigate("/zbc/new")} testId="quick-action-new-zbc" />
                    <ActionCard icon={Plus} label="New RFQ" description="Build a commercial offer" onClick={() => navigate("/rfq/new")} testId="quick-action-new-rfq" />
                    <ActionCard icon={Plus} label="New Settlement" description="Prepare quarterly revision" onClick={() => navigate("/settlement/new")} testId="quick-action-new-settlement" />
                </div>
            </Section>
        </>
    );
}

// ============================================================
// FINANCE DASHBOARD
// ============================================================
function FinanceDashboard({ kpis, navigate }) {
    return (
        <>
            <Section eyebrow="MY WORK">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Tile label="MY ZBCS" icon={Calculator}
                          value={fmtIN(kpis.my_zbcs_total || 0)}
                          sub={`${kpis.my_zbcs_draft || 0} draft · ${kpis.my_zbcs_verified || 0} verified`}
                          onClick={() => navigate("/zbc")} footerLink testId="kpi-finance-zbcs" />
                    <Tile label="MY RFQS" icon={FileText}
                          value={fmtIN(kpis.my_rfqs_total || 0)}
                          sub={`${kpis.my_rfqs_pending_dh || 0} awaiting DH · ${kpis.my_rfqs_approved || 0} approved`}
                          onClick={() => navigate("/rfq")} footerLink testId="kpi-finance-rfqs" />
                    <Tile label="MY SETTLEMENTS" icon={Wallet}
                          value={fmtIN(kpis.my_settlements_quarter || 0)} accent="this quarter"
                          sub={`${kpis.my_settlements_review || 0} pending director · ${kpis.my_settlements_locked || 0} locked`}
                          onClick={() => navigate("/settlement")} footerLink testId="kpi-finance-settlements" />
                </div>
            </Section>

            <Section eyebrow="ALERTS">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Tile label="PARTS FLAGGED" icon={AlertTriangle} tone={kpis.my_zbcs_red > 0 ? "red" : "slate"}
                          value={fmtIN(kpis.my_zbcs_red || 0)} accent="RED variance"
                          sub={`${kpis.stale_quotes_count || 0} stale quotes globally`}
                          onClick={() => navigate("/zbc/variance")} footerLink testId="kpi-finance-flags" />
                    <Tile label="RFQS DUE SOON" icon={Clock} tone={kpis.my_rfqs_due_24h > 0 ? "amber" : "slate"}
                          value={fmtIN(kpis.my_rfqs_due_24h || 0)} accent="within 24h"
                          sub={`${kpis.my_rfqs_due_48h || 0} due within 48 hours`}
                          onClick={() => navigate("/rfq")} footerLink testId="kpi-finance-due" />
                </div>
            </Section>

            <AnomalyDetectorCard navigate={navigate} />

            <Section eyebrow="QUICK ACTIONS">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ActionCard icon={Plus} label="New ZBC" description="Start a should-cost analysis" onClick={() => navigate("/zbc/new")} testId="quick-action-new-zbc" />
                    <ActionCard icon={Plus} label="New RFQ" description="Build a commercial offer" onClick={() => navigate("/rfq/new")} testId="quick-action-new-rfq" />
                    <ActionCard icon={Plus} label="New Settlement" description="Prepare quarterly revision" onClick={() => navigate("/settlement/new")} testId="quick-action-new-settlement" />
                </div>
            </Section>
        </>
    );
}

// ============================================================
// DEPT HEAD DASHBOARD
// ============================================================
function DeptHeadDashboard({ kpis, navigate }) {
    const list = kpis.pending_my_approval_list || [];
    return (
        <>
            <Section eyebrow="MY APPROVALS">
                <Card className="p-5" testId="kpi-dh-pending-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Bell className="h-4 w-4 text-[hsl(var(--copper))]" />
                        <div className="font-display text-[15px] font-semibold text-slate-900">RFQs Pending My Approval</div>
                        <Pill tone="copper">{kpis.pending_my_approval || 0}</Pill>
                    </div>
                    {list.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-400">No pending RFQs — you&rsquo;re all caught up.</div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {list.map((r) => (
                                <div key={r.id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`dh-pending-${r.reference}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono font-semibold text-[13px] text-slate-900">{r.reference}</div>
                                        <div className="text-[11px] text-slate-500">{r.customer_code} · {fmtINR(r.total_price || 0)} · Margin {marginPill(r.pricing?.margin_pct)}</div>
                                    </div>
                                    <button onClick={() => navigate(`/rfq/${r.id}`)} className="h-7 px-3 rounded-md bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-white text-[12px] font-medium">Review</button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-slate-100">
                        <button onClick={() => navigate("/rfq/pending")} className="text-[11px] font-medium text-[hsl(var(--copper))] hover:underline" data-testid="dh-view-all-pending">View all pending approvals →</button>
                    </div>
                </Card>
            </Section>

            <Section eyebrow="HISTORY">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Tile label="APPROVED THIS MONTH" icon={CheckCircle} tone="emerald"
                          value={fmtIN(kpis.approved_this_month || 0)}
                          sub={`${kpis.escalated_this_month || 0} escalated to Director`}
                          onClick={() => navigate("/rfq")} footerLink testId="kpi-dh-approved" />
                    <Tile label="HELD RFQS" icon={AlertCircle} tone={kpis.held_rfqs > 0 ? "amber" : "slate"}
                          value={fmtIN(kpis.held_rfqs || 0)} accent="awaiting Finance"
                          sub="Sent back with comments"
                          onClick={() => navigate("/rfq")} footerLink testId="kpi-dh-held" />
                </div>
            </Section>

            <AnomalyDetectorCard navigate={navigate} />
        </>
    );
}

// ============================================================
// DIRECTOR DASHBOARD
// ============================================================
function DirectorDashboard({ kpis, navigate }) {
    const sl = kpis.pending_settlement_list || [];
    const rl = kpis.low_margin_rfq_list || [];
    return (
        <>
            <Section eyebrow="PENDING DIRECTOR ACTION">
                <Card className="p-5 border-l-4 border-l-[hsl(var(--copper))]" testId="kpi-director-pending-card">
                    <div className="space-y-5">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Bell className="h-4 w-4 text-[hsl(var(--copper))]" />
                                <div className="font-display text-[14px] font-semibold text-slate-900">Settlements to Sign-Off</div>
                                <Pill tone="copper">{kpis.pending_settlements || 0}</Pill>
                            </div>
                            {sl.length === 0 ? (
                                <div className="text-[12px] text-slate-400 py-2">No settlements awaiting your sign-off.</div>
                            ) : sl.map((s) => (
                                <div key={s.id} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-md hover:bg-slate-50" data-testid={`director-pending-sett-${s.reference}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono font-semibold text-[13px]">{s.reference}</div>
                                        <div className="text-[11px] text-slate-500">{s.customer_code} · {fmtINR(s.computed?.total_claim || 0)} claim · Submitted {fmtRelative(s.submitted_at)}</div>
                                    </div>
                                    <button onClick={() => navigate(`/settlement/${s.id}`)} className="h-7 px-3 rounded-md bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-white text-[12px] font-medium">Review &amp; Sign-Off</button>
                                </div>
                            ))}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <ArrowUpRight className="h-4 w-4 text-amber-600" />
                                <div className="font-display text-[14px] font-semibold text-slate-900">RFQs Escalated to Me</div>
                                <Pill tone="amber">{kpis.low_margin_rfqs || 0}</Pill>
                            </div>
                            {rl.length === 0 ? (
                                <div className="text-[12px] text-slate-400 py-2">No escalations pending.</div>
                            ) : rl.map((r) => (
                                <div key={r.id} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-md hover:bg-slate-50" data-testid={`director-pending-rfq-${r.reference}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono font-semibold text-[13px]">{r.reference}</div>
                                        <div className="text-[11px] text-slate-500">{r.customer_code} · Margin {marginPill(r.pricing?.margin_pct)} (below {8}% threshold)</div>
                                    </div>
                                    <button onClick={() => navigate(`/rfq/${r.id}`)} className="h-7 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-[12px] font-medium">Review</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            </Section>

            <Section eyebrow="BUSINESS OVERVIEW">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Tile label="DMC IMPACT · QUARTER" icon={TrendingUp} tone="copper"
                          value={fmtINR(kpis.dmc_impact_quarter || 0, 0)}
                          sub="ABC COMPANY is absorbing this across customers"
                          onClick={() => navigate("/settlement")} footerLink testId="kpi-director-dmc" />
                    <Tile label="ACTIVE RFQs" icon={FileText}
                          value={fmtIN(kpis.open_rfqs || 0)}
                          sub={kpis.biggest_active_rfq ? `Largest: ${fmtINR(kpis.biggest_active_rfq.total_price || 0)} (${kpis.biggest_active_rfq.customer_code})` : "—"}
                          onClick={() => navigate("/rfq")} footerLink testId="kpi-director-rfqs" />
                    <Tile label="SETTLEMENTS · QUARTER" icon={Wallet}
                          value={fmtIN(kpis.settlements_approved_quarter || 0)} accent="approved"
                          sub={`${fmtINR(kpis.settlement_claim_quarter || 0)} total claim value`}
                          onClick={() => navigate("/settlement")} footerLink testId="kpi-director-settlements" />
                </div>
            </Section>

            <AnomalyDetectorCard navigate={navigate} />
        </>
    );
}

// ============================================================
// PLANT MANAGER DASHBOARD
// ============================================================
function PlantDashboard({ kpis, navigate }) {
    const ml = kpis.machines_needing_input || [];
    return (
        <>
            <Section eyebrow="MY PLANT">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Tile label="MY PLANT" icon={Factory}
                          value={kpis.my_plant?.code || "—"}
                          sub={`${kpis.machines_total || 0} machines registered`}
                          onClick={() => navigate("/plants")} footerLink testId="kpi-plant-info" />
                    <Tile label="MACHINE RATES STATUS" icon={Hammer}
                          tone={kpis.machines_rate_missing > 0 ? "red" : "emerald"}
                          value={fmtIN(kpis.machines_rate_approved || 0)} accent="approved ✓"
                          sub={`${kpis.machines_rate_pending || 0} pending · ${kpis.machines_rate_draft || 0} draft · ${kpis.machines_rate_missing || 0} no rate yet`}
                          onClick={() => navigate("/machine-rates")} footerLink testId="kpi-plant-rates" />
                </div>
            </Section>

            {ml.length > 0 && (
                <Section eyebrow="ACTIONS — MACHINES NEEDING RATE INPUT">
                    <Card className="divide-y divide-slate-100" testId="plant-machines-needing-input">
                        {ml.map((m) => (
                            <div key={m.machine_id} className="flex items-center justify-between p-3.5 hover:bg-slate-50" data-testid={`plant-machine-${m.code}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="font-mono font-semibold text-[13px]">{m.code}</div>
                                    <div className="text-[11px] text-slate-500">{m.name} · Status: <span className={m.status === "NO_RATE" ? "text-red-600 font-medium" : m.status === "DRAFT" ? "text-slate-600 font-medium" : "text-amber-600 font-medium"}>{m.status.replace("_", " ")}</span></div>
                                </div>
                                <button onClick={() => navigate("/machine-rates")} className="h-7 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-[12px] font-medium">
                                    {m.status === "NO_RATE" ? "Enter Rate" : m.status === "DRAFT" ? "Continue Entry" : "View Status"}
                                </button>
                            </div>
                        ))}
                    </Card>
                </Section>
            )}
        </>
    );
}

// ============================================================
// PURCHASE MANAGER DASHBOARD
// ============================================================
function PurchaseDashboard({ kpis, navigate }) {
    return (
        <>
            <Section eyebrow="OVERVIEW (READ-ONLY)">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Tile label="SUPPLIER QUOTES" icon={Package}
                          value={fmtIN(kpis.sq_total || 0)} accent="on file"
                          sub={`${kpis.sq_stale || 0} stale (>180 days)`}
                          onClick={() => navigate("/supplier-quotes")} footerLink testId="kpi-purchase-quotes" />
                    <Tile label="VARIANCE SUMMARY" icon={AlertTriangle}
                          tone={kpis.variance_red > 0 ? "red" : "slate"}
                          value={fmtIN(kpis.variance_red || 0)} accent="RED"
                          sub={`${kpis.variance_flag || 0} FLAG · ${kpis.variance_green || 0} GREEN (fair)`}
                          onClick={() => navigate("/zbc/variance")} footerLink testId="kpi-purchase-variance" />
                    <Tile label="STEEL RATES" icon={Hammer}
                          value={kpis.steel_rate_monitor?.[0] ? `${fmtINR(kpis.steel_rate_monitor[0].rate_per_kg)}/kg` : "—"}
                          sub={kpis.steel_rate_monitor?.slice(0, 3).map((g) => g.code).join(" · ")}
                          onClick={() => navigate("/steel-grades")} footerLink testId="kpi-purchase-steel" />
                </div>
            </Section>

            <AnomalyDetectorCard navigate={navigate} />
        </>
    );
}

function Section({ eyebrow, children }) {
    return (
        <section>
            <div className="eyebrow text-slate-500 mb-3">{eyebrow}</div>
            {children}
        </section>
    );
}
